
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useAuth } from "./global_varibles";
import { useEffect, useState } from "react";
import HeaderAppBar from "./appbar";
import { BackendBaseUrl, ORANGE, BLACK } from "./global_varibles";
import { BarChart3, TrendingUp } from "lucide-react";

// Define a loose summary data shape (adjust as needed)
interface SummaryData {
  [key: string]: string | number;
}

interface GenAdvProps {
  summary_data: SummaryData;
  selected_date: Dayjs;
}

function GenarateAdvSummaryData({ summary_data, selected_date }: GenAdvProps) {
  if (!summary_data || Object.keys(summary_data).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-4">
          <BarChart3 className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-gray-900 text-xl font-bold mb-2">No Data Available</h3>
        <p className="text-gray-500 text-center">
          No summary data found for {selected_date.format("MMMM DD, YYYY")}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4">
      {/* Header Section */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            Advanced Summary
          </h2>
        </div>
        <div className="h-1.5 w-24 bg-gradient-to-r from-orange-500 to-orange-600 mx-auto rounded-full mb-3 shadow-sm shadow-orange-500/20"></div>
        <p className="text-gray-600 font-medium">
          Detailed analysis for {selected_date.format("MMMM DD, YYYY")}
        </p>
      </div>

      {/* Summary Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(summary_data).map(([key, value]) => (
          <div key={key} className="group">
            <div className="bg-white border border-gray-100 hover:border-orange-500/40 rounded-2xl p-6 transition-all duration-400 hover:-translate-y-2 shadow-sm hover:shadow-2xl hover:shadow-orange-500/10 relative overflow-hidden group">
              <div className="absolute top-0 left-0 w-full h-1 bg-gray-50 group-hover:bg-orange-500 transition-colors"></div>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full shadow-[0_0_8px_rgba(249,115,22,0.6)]"></div>
                    <h3 className="text-gray-900 text-lg font-bold capitalize group-hover:text-orange-600 transition-colors duration-300">
                      {key.replace(/_/g, " ")}
                    </h3>
                  </div>
                </div>
                <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center border border-orange-100">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 transition-colors group-hover:bg-orange-50/30">
                <div className="text-3xl font-black text-gray-900 mb-1 tracking-tight">
                  {typeof value === 'number' && value > 1000 
                    ? `₹${value.toLocaleString()}` 
                    : value
                  }
                </div>
                <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Current value</p>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
}

function DisplayAdvanceSummaryData() {
  const { isAuthinticated, user_data } = useAuth();
  const [summary_data, setSummmaryData] = useState<SummaryData>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());

  useEffect(() => {
    if (isAuthinticated && user_data?.id != null) {
      const GetSummaryData = async () => {
        try {
          setIsLoading(true);
          const data_to_get = {
            req_id: user_data.id,
            adv_summ: true,
            date: selectedDate.toISOString(),
          };
          const bkroute = `${BackendBaseUrl}orders-summary`;
          const response = await fetch(bkroute, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data_to_get),
          });
          const response_data = await response.json();
          if (response.ok && response_data?.summary) {
            setSummmaryData(response_data.summary as SummaryData);
          } else {
            setSummmaryData({});
          }
        } catch {
          setSummmaryData({});
        } finally {
          setIsLoading(false);
        }
      };
      GetSummaryData();
    } else {
      setIsLoading(false);
    }
  }, [isAuthinticated, user_data?.id, selectedDate]);

  if (!isAuthinticated) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="bg-white border border-gray-100 rounded-2xl p-8 text-center shadow-xl">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-500">Please login to view this page</p>
        </div>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="min-h-screen bg-[#f8fafc]">
        {/* Dot Pattern Background */}
        <div
          className="absolute inset-0 opacity-[0.02] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='28' height='28' viewBox='0 0 28 28' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <HeaderAppBar />
        
        {/* Date Picker Section */}
        <div className="pt-8 pb-4 px-4 relative">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-center">
              <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-xl shadow-black/5">
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  format="MM/DD/YYYY"
                  onChange={(newDate) => setSelectedDate(newDate ?? selectedDate)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      InputProps: {
                        sx: {
                          color: BLACK,
                          fontWeight: 700,
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          '& input': {
                            color: BLACK,
                            WebkitTextFillColor: BLACK,
                            fontWeight: 700,
                          },
                          '& input::placeholder': {
                            color: 'rgba(0,0,0,0.4)',
                            opacity: 1,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(0, 0, 0, 0.08)',
                            borderWidth: '2px',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: ORANGE,
                            borderWidth: '2px',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: ORANGE,
                            borderWidth: '2px',
                          },
                          '& .MuiSvgIcon-root': {
                            color: ORANGE,
                            fontSize: '1.8rem',
                          },
                        },
                      },
                      InputLabelProps: {
                        sx: {
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontSize: '1rem',
                          fontWeight: 600,
                          '&.Mui-focused': {
                            color: ORANGE,
                            fontWeight: 800,
                          },
                        },
                      },
                    },
                    popper: {
                      sx: {
                        '& .MuiPaper-root': {
                          backgroundColor: '#1f2937',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          '& .MuiPickersDay-root': {
                            color: 'white',
                            '&:hover': { backgroundColor: 'rgba(249, 115, 22, 0.2)' },
                            '&.Mui-selected': {
                              backgroundColor: '#f97316 !important',
                              color: 'black',
                              fontWeight: 700,
                            },
                          },
                          '& .MuiPickersCalendarHeader-root': { color: 'white' },
                          '& .MuiPickersCalendarHeader-label': { color: 'white', fontWeight: 600 },
                          '& .MuiDayCalendar-weekDayLabel': { color: '#f97316', fontWeight: 600 },
                          '& .MuiPickersArrowSwitcher-button': { color: '#f97316' },
                        },
                      },
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="px-4 py-8 relative">
          {isLoading ? (
            <div className="flex flex-col justify-center items-center py-20 gap-4">
              <div className="relative">
                <div className="w-20 h-20 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-10 h-10 bg-orange-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              <p className="text-gray-900 text-lg font-bold">Loading advanced summary...</p>
            </div>
          ) : (
            <GenarateAdvSummaryData
              summary_data={summary_data}
              selected_date={selectedDate}
            />
          )}
        </div>
      </div>
    </LocalizationProvider>
  );
}

export default DisplayAdvanceSummaryData;


